import { get, set } from './index';

export const VIEW_STATE_SLICES = ['filters', 'sort', 'pagination'] as const;

export type ViewStateSlice = (typeof VIEW_STATE_SLICES)[number];

export type PersistedPagination = {
  page?: number;
  pageSize?: number;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

export const isViewStateBlob = (value: unknown): value is Record<string, unknown> =>
  isPlainObject(value) && VIEW_STATE_SLICES.some(slice => slice in value);

const isLegacySort = (value: unknown): boolean => {
  if (!isPlainObject(value) || isViewStateBlob(value)) return false;
  const { key, order } = value;
  return typeof key === 'string' && Boolean(key) && (order === 'asc' || order === 'desc');
};

const isPositiveInt = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 1;

const toViewStateBlob = (stored: unknown): Record<string, unknown> => {
  if (isViewStateBlob(stored)) return { ...stored };
  if (isLegacySort(stored)) return { sort: stored };
  if (isPlainObject(stored)) return { filters: stored };
  return {};
};

export const parsePersistedPagination = (stored: unknown): PersistedPagination => {
  if (!isPlainObject(stored)) return {};

  const parsed: PersistedPagination = {};
  if (isPositiveInt(stored.page)) parsed.page = stored.page;
  if (isPositiveInt(stored.pageSize)) parsed.pageSize = stored.pageSize;
  return parsed;
};

export const getViewSlice = (viewKey: string, slice: ViewStateSlice): unknown => {
  const stored = get(viewKey, null);
  if (!isPlainObject(stored)) return undefined;
  if (isViewStateBlob(stored)) return stored[slice];
  if (slice === 'sort' && isLegacySort(stored)) return stored;
  if (slice === 'filters' && !isLegacySort(stored)) return stored;
  return undefined;
};

export const setViewSlice = (viewKey: string, slice: ViewStateSlice, value: unknown): void => {
  const blob = toViewStateBlob(get(viewKey, null));
  set(viewKey, { ...blob, [slice]: value });
};

export const migrateLegacyListViewState = (
  viewKey: string,
  legacyKeys: { filters?: string; sort?: string },
): void => {
  const existing = get(viewKey, null);
  if (isViewStateBlob(existing)) return;

  const blob = toViewStateBlob(existing);

  if (legacyKeys.filters) {
    const filters = get(legacyKeys.filters, null);
    if (filters != null && blob.filters == null) blob.filters = filters;
  }
  if (legacyKeys.sort) {
    const sort = get(legacyKeys.sort, null);
    if (sort != null && blob.sort == null) blob.sort = sort;
  }

  if (Object.keys(blob).length === 0) return;
  set(viewKey, blob);
};
